/* ==========================================================================
   RAO SCENTS — Site Behaviour
   --------------------------------------------------------------------------
   Sections:
     01. PAGE LOADER
     02. HEADER SCROLL STATE
     03. MOBILE NAVIGATION
     04. SCROLL REVEAL (IntersectionObserver)
     05. ACTIVE NAV HIGHLIGHT
     06. FEATURED PRODUCT RENDER (homepage)

   No dependencies. Everything is progressive — if JS fails, content still
   renders (see the .no-js fallback at the bottom of each page's <head>).
   ========================================================================== */

(function () {
  'use strict';

  /* Respect the OS "reduce motion" setting throughout. */
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;


  /* ======================================================================
     01. PAGE LOADER — logo reveal, then fade out once assets are in
     ====================================================================== */
  const loader = document.querySelector('[data-loader]');
  const hero   = document.querySelector('[data-hero]');

  function dismissLoader() {
    if (loader) loader.classList.add('is-done');
    document.body.classList.remove('is-locked');
    if (hero) hero.classList.add('is-ready');       // triggers hero staging
  }

  if (loader) {
    document.body.classList.add('is-locked');
    // Minimum on-screen time so the reveal reads as intentional, not a flash.
    const MIN_MS = prefersReduced ? 0 : 900;
    const start  = Date.now();

    window.addEventListener('load', function () {
      const wait = Math.max(0, MIN_MS - (Date.now() - start));
      setTimeout(dismissLoader, wait);
    });

    // Safety net: never trap the user behind the loader.
    setTimeout(dismissLoader, 3500);
  } else if (hero) {
    requestAnimationFrame(() => hero.classList.add('is-ready'));
  }


  /* ======================================================================
     02. HEADER SCROLL STATE — condense + frost after a short scroll
     ====================================================================== */
  const header = document.querySelector('[data-header]');

  if (header) {
    let ticking = false;
    const onScroll = function () {
      if (ticking) return;
      ticking = true;
      // rAF-throttled: one class toggle per frame at most.
      requestAnimationFrame(function () {
        header.classList.toggle('is-stuck', window.scrollY > 40);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }


  /* ======================================================================
     03. MOBILE NAVIGATION
     ====================================================================== */
  const burger = document.querySelector('[data-burger]');
  const nav    = document.querySelector('[data-nav]');

  if (burger && nav) {
    const setNav = function (open) {
      burger.classList.toggle('is-open', open);
      nav.classList.toggle('is-open', open);
      burger.setAttribute('aria-expanded', String(open));
      document.body.classList.toggle('is-locked', open);
    };

    burger.addEventListener('click', function () {
      setNav(!nav.classList.contains('is-open'));
    });

    // Close when a link is tapped, or on Escape.
   nav.addEventListener('click', function (e) {
      const parentLink = e.target.closest('.nav__item.has-children > .nav__link');
      if (parentLink && window.innerWidth <= 989) {
        e.preventDefault();
        const item = parentLink.closest('.nav__item');
        const isOpen = item.classList.toggle('is-open');
        const toggle = item.querySelector('.nav__toggle');
        if (toggle) toggle.setAttribute('aria-expanded', String(isOpen));
        return;
      }
      if (e.target.closest('a')) setNav(false);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') setNav(false);
    });
    /* Submenu accordion toggle (e.g. Shop -> Men/Women/Unisex) */
    document.querySelectorAll('.nav__toggle').forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        const item = toggle.closest('.nav__item');
        const isOpen = item.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(isOpen));
      });
    });
  }


  /* ======================================================================
     04. SCROLL REVEAL — fade + slide-up as sections enter the viewport
     Uses IntersectionObserver (no scroll listener), unobserves after firing,
     and animates opacity/transform only so it stays cheap on mobile.
     ====================================================================== */
  const revealTargets = document.querySelectorAll('[data-reveal], [data-reveal-group]');

  if (prefersReduced || !('IntersectionObserver' in window)) {
    revealTargets.forEach(el => el.classList.add('is-visible'));
  } else {
    const io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);          // one-shot: no repeat cost
      });
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -8% 0px'          // fire slightly before full entry
    });

    revealTargets.forEach(el => io.observe(el));
  }


  /* ======================================================================
     05. ACTIVE NAV HIGHLIGHT — marks the current page in the header
     ====================================================================== */
  const here = location.pathname.split('/').pop() || 'index.html';
  document.querySelectorAll('.nav__link').forEach(function (link) {
    const target = link.getAttribute('href');
    if (target && target.split('#')[0] === here) link.classList.add('is-active');
  });


  /* ======================================================================
     06. HOMEPAGE PRODUCT RENDERING — from products.js
     Featured row: navy promo card (static in HTML) + one pick per series.
     Signature grid: the remaining fragrances.
     ====================================================================== */
  const featuredAnchor = document.querySelector('[data-featured-anchor]');
  const signatureMount = document.querySelector('[data-signature]');

  if (typeof RAO_PRODUCTS !== 'undefined') {

    const featuredSlugs = ['axis', 'afsana', 'royal-trace'];

    if (featuredAnchor) {
      const featured = featuredSlugs
        .map(slug => RAO_PRODUCTS.find(p => p.slug === slug))
        .filter(Boolean);
      featuredAnchor.insertAdjacentHTML('beforebegin', featured.map(productCardHTML).join(''));
    }

    if (signatureMount) {
      const rest = RAO_PRODUCTS.filter(p => !featuredSlugs.includes(p.slug));
      signatureMount.innerHTML = rest.map(productCardHTML).join('');
    }
  }


  /* ======================================================================
     06b. HERO BANNER CAROUSEL — crossfades [data-slide] layers
     Dots are generated from the slide count, clickable, and kept in sync.
     Auto-advances every 6s; pauses while the tab is hidden and skips
     auto-play entirely under prefers-reduced-motion.
     ====================================================================== */
  const slides   = document.querySelectorAll('[data-slide]');
  const captions = document.querySelectorAll('[data-caption]');
  const dotsWrap = document.querySelector('[data-dots]');

  if (slides.length > 1 && dotsWrap) {
    let current = 0;
    let timer   = null;

    // Build one dot per slide
    slides.forEach(function (_, i) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', 'Show banner ' + (i + 1));
      if (i === 0) dot.classList.add('is-active');
      dot.addEventListener('click', function () { go(i); restart(); });
      dotsWrap.appendChild(dot);
    });
    const dots = dotsWrap.querySelectorAll('button');

    function go(i) {
      slides[current].classList.remove('is-active');
      dots[current].classList.remove('is-active');
      if (captions[current]) {
        captions[current].classList.remove('is-active');
        captions[current].setAttribute('aria-hidden', 'true');
      }
      current = i % slides.length;
      slides[current].classList.add('is-active');   // re-adding restarts the zoom
      dots[current].classList.add('is-active');
      if (captions[current]) {
        captions[current].classList.add('is-active');
        captions[current].removeAttribute('aria-hidden');
      }

      // Per-slide layout: slides with data-layout="right" move the caption
      // and CTA to the right half of the card (see .is-right in the CSS).
      const card = slides[current].closest('.hero__card');
      if (card) card.classList.toggle('is-right', slides[current].dataset.layout === 'right');
    }

    function restart() {
      if (timer) clearInterval(timer);
      if (prefersReduced) return;                   // no auto-play if reduced motion
      // The hidden-check (rather than clearing the timer on visibilitychange)
      // keeps the carousel resilient — it simply resumes on the next tick.
      timer = setInterval(function () {
        if (!document.hidden) go(current + 1);
      }, 6000);
    }

    restart();
  }


  /* ======================================================================
     06c. ANNOUNCEMENT BAR ROTATOR — crossfades [data-topbar-msg] messages
     ====================================================================== */
  const topbarMsgs = document.querySelectorAll('[data-topbar-msg]');
  if (topbarMsgs.length > 1 && !prefersReduced) {
    let tIdx = 0;
    setInterval(function () {
      if (document.hidden) return;
      topbarMsgs[tIdx].classList.remove('is-active');
      tIdx = (tIdx + 1) % topbarMsgs.length;
      topbarMsgs[tIdx].classList.add('is-active');
    }, 4000);
  }


  /* ======================================================================
     06d. CATEGORY PAGES — grid + sort for men/women/unisex landing pages
     Mount: [data-category="<Series>"]; optional [data-sort] + [data-count].
     ====================================================================== */
  const catMount = document.querySelector('[data-category]');

  if (catMount && typeof RAO_PRODUCTS !== 'undefined') {
    const series  = catMount.getAttribute('data-category');
    const sortSel = document.querySelector('[data-sort]');
    const countEl = document.querySelector('[data-count]');
    const items   = RAO_PRODUCTS.filter(p => p.series === series);

    function renderCategory() {
      const sorted = items.slice();
      const mode = sortSel ? sortSel.value : 'featured';
      if (mode === 'price-asc')  sorted.sort((a, b) => a.price - b.price);
      if (mode === 'price-desc') sorted.sort((a, b) => b.price - a.price);
      if (mode === 'name')       sorted.sort((a, b) => a.name.localeCompare(b.name));
      catMount.innerHTML = sorted.map(productCardHTML).join('');
    }

    if (countEl) countEl.textContent = items.length + ' Fragrances';
    if (sortSel) sortSel.addEventListener('change', renderCategory);
    renderCategory();
  }


  /* ======================================================================
     06e. HOMEPAGE CATEGORY CATALOGUES — 3 products per gender preview
     Mount: [data-catalogue="Men|Women|Unisex"]
     ====================================================================== */
  document.querySelectorAll('[data-catalogue]').forEach(function (mount) {
    if (typeof RAO_PRODUCTS === 'undefined') return;
    const gender = mount.getAttribute('data-catalogue');
    const picks  = RAO_PRODUCTS.filter(p => p.gender === gender).slice(0, 3);
    mount.innerHTML = picks.map(productCardHTML).join('');
  });


  /* ======================================================================
     07. SHOP PAGE — one grid per series, mounted on [data-series="..."]
     ====================================================================== */
  document.querySelectorAll('[data-series]').forEach(function (mount) {
    if (typeof RAO_PRODUCTS === 'undefined') return;
    const series = mount.getAttribute('data-series');
    const items  = RAO_PRODUCTS.filter(p => p.series === series);
    mount.innerHTML = items.map(productCardHTML).join('');
  });


  /* ======================================================================
     08. PRODUCT DETAIL PAGE — renders from ?p=<slug>
     Mounts on [data-product]. Falls back to the first product if the slug
     is missing or unknown, so the template never renders empty.
     ====================================================================== */
  const pdMount = document.querySelector('[data-product]');

  if (pdMount && typeof RAO_PRODUCTS !== 'undefined') {
    const slug    = new URLSearchParams(location.search).get('p');
    const product = RAO_PRODUCTS.find(p => p.slug === slug) || RAO_PRODUCTS[0];

    const notesBlock = (label, list) => `
      <div class="note-card">
        <h4><i></i>${label} Notes</h4>
        <ul>${list.map(n => `<li>${n}</li>`).join('')}</ul>
      </div>`;

    /* -- SEO: title, meta description, canonical-ish product schema -- */
    document.title = product.name + ' — ' + product.tagline + ' | Rao Scents Eau de Parfum';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content',
        product.name + ' by Rao Scents — ' + product.tagline + ' Long-lasting ' +
        product.gender.toLowerCase() + "'s eau de parfum with 40% oil concentration. " +
        'Top notes: ' + product.notes.top.join(', ') + '. ' +
        formatPKR(product.price) + ', 50 ml. Cash on delivery across Pakistan.');
    }

    /* Product structured data for search engines */
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.description,
      image: location.origin + location.pathname.replace(/[^/]*$/, '') + product.image,
      brand: { '@type': 'Brand', name: 'Rao Scents' },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'PKR',
        price: product.price,
        availability: 'https://schema.org/InStock'
      }
    });
    document.head.appendChild(ld);

    /* -- 3-slot gallery: bottle / boxed (when supplied) / side-by-side -- */
    const gallery = [];
    if (product.images) {
      if (product.images.bottle) gallery.push({ src: product.images.bottle, label: 'Bottle' });
      if (product.images.boxed)  gallery.push({ src: product.images.boxed,  label: 'In the box' });
      if (product.images.pair)   gallery.push({ src: product.images.pair,   label: 'Bottle & box' });
    }
    if (gallery.length === 0) gallery.push({ src: product.image, label: product.name });

    const thumbsHTML = gallery.length > 1 ? `
      <div class="pd__thumbs" role="tablist" aria-label="Product photos">
        ${gallery.map((g, i) => `
          <button class="pd__thumb${i === 0 ? ' is-active' : ''}" type="button" data-thumb="${i}" aria-label="${g.label}">
            <img src="${g.src}" alt="${product.name} — ${g.label}" loading="lazy" width="200" height="200">
          </button>`).join('')}
      </div>` : '';

    pdMount.innerHTML = `
      <!-- Breadcrumb -->
      <nav class="crumbs" aria-label="Breadcrumb">
        <a href="index.html">Home</a><i>/</i>
        <a href="shop.html">Shop</a><i>/</i>
        <a href="${product.gender === 'Men' ? 'men.html' : product.gender === 'Women' ? 'women.html' : 'unisex.html'}">${product.gender}</a><i>/</i>
        <strong>${product.name}</strong>
      </nav>

      <div class="pd">

        <!-- Gallery: main image + thumbnails -->
        <div class="pd__media" data-reveal>
          <div class="pd__main-img">
            <span class="product-card__tag">${product.gender}</span>
            <img src="${gallery[0].src}" alt="${product.name} eau de parfum by Rao Scents — ${gallery[0].label}" width="1080" height="1080" data-main-img>
          </div>
          ${thumbsHTML}
        </div>

        <!-- Info -->
        <div class="pd__info" data-reveal style="--d:120ms">
          <span class="pill">The ${product.series} Series</span>
          <h1 class="pd__name">${product.name}</h1>
          <p class="pd__tagline">${product.tagline}</p>

          <div class="pd__pricerow">
            <span class="pd__price">${formatPKR(product.price)}</span>
            <span class="pd__size">${product.size} &middot; Eau de Parfum</span>
          </div>

          <p class="pd__desc">${product.description}</p>

          <!-- Scent pyramid -->
          <div class="notes">
            ${notesBlock('Top',   product.notes.top)}
            ${notesBlock('Heart', product.notes.heart)}
            ${notesBlock('Base',  product.notes.base)}
          </div>

          <!-- Purchase (visual placeholder — wire to Shopify cart later) -->
          <div class="pd__buy">
            <span class="qty">
              <button type="button" data-qty="-1" aria-label="Decrease quantity">&minus;</button>
              <output data-qty-value>1</output>
              <button type="button" data-qty="1" aria-label="Increase quantity">+</button>
            </span>
            <button class="btn btn--gold" type="button" data-add-cart>Add to Cart</button>
            <a class="btn btn--outline pd__wa"
               href="https://wa.me/923000000000?text=${encodeURIComponent('Hi Rao Scents, I\'d like to order ' + product.name + '.')}"
               target="_blank" rel="noopener">Order on WhatsApp</a>
          </div>

          <div class="pd__meta">
            <span><i>&#10003;</i> 100% original</span>
            <span><i>&#10003;</i> Cash on delivery</span>
            <span><i>&#10003;</i> Ships in 2&ndash;4 days</span>
          </div>
        </div>

      </div>`;

    /* -- Gallery thumbnail switching -- */
    const mainImg = pdMount.querySelector('[data-main-img]');
    pdMount.querySelectorAll('[data-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        const g = gallery[parseInt(thumb.dataset.thumb, 10)];
        mainImg.src = g.src;
        mainImg.alt = product.name + ' eau de parfum by Rao Scents — ' + g.label;
        pdMount.querySelectorAll('[data-thumb]').forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });

    /* -- Quantity stepper -- */
    const qtyOut = pdMount.querySelector('[data-qty-value]');
    pdMount.querySelectorAll('[data-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const next = Math.min(9, Math.max(1, parseInt(qtyOut.value, 10) + parseInt(btn.dataset.qty, 10)));
        qtyOut.value = next;
      });
    });

    /* -- "Add to Cart" visual feedback (placeholder only) -- */
    const addBtn = pdMount.querySelector('[data-add-cart]');
    addBtn.addEventListener('click', function () {
      const original = addBtn.textContent;
      addBtn.textContent = 'Added ✓';
      addBtn.classList.add('is-added');
      setTimeout(function () {
        addBtn.textContent = original;
        addBtn.classList.remove('is-added');
      }, 1600);
    });

    /* -- Related products: same series, excluding the current one -- */
    const relatedMount = document.querySelector('[data-related]');
    if (relatedMount) {
      const related = RAO_PRODUCTS
        .filter(p => p.series === product.series && p.slug !== product.slug)
        .slice(0, 4);
      relatedMount.innerHTML = related.map(productCardHTML).join('');
    }

    /* Late-added [data-reveal] nodes need observing too — simplest reliable
       path on a rendered-at-load page: reveal them immediately. */
    pdMount.querySelectorAll('[data-reveal]').forEach(el => el.classList.add('is-visible'));
  }

})();


/* ==========================================================================
   SHOPIFY ADDITIONS — server-rendered product form + gallery behaviour
   (The static-site renderers above never fire on Shopify — their mounts
   like [data-product] and [data-category] don't exist in Liquid templates.)
   ========================================================================== */
(function () {
  'use strict';

  /* Quantity stepper wired to the real <input name="quantity"> */
  const qtyInput = document.querySelector('[data-qty-input]');
  if (qtyInput) {
    document.querySelectorAll('[data-qty-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const next = Math.min(9, Math.max(1, parseInt(qtyInput.value || '1', 10) + parseInt(btn.dataset.qtyStep, 10)));
        qtyInput.value = next;
      });
    });
  }

  /* Product gallery: Liquid renders [data-pthumb] buttons + [data-pmain] */
  const pMain = document.querySelector('[data-pmain]');
  if (pMain) {
    document.querySelectorAll('[data-pthumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        pMain.src = thumb.dataset.pthumb;
        if (thumb.dataset.alt) pMain.alt = thumb.dataset.alt;
        document.querySelectorAll('[data-pthumb]').forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  }

  /* Collection sort: select navigates with Shopify's sort_by param */
  const sortSel = document.querySelector('[data-shopify-sort]');
  if (sortSel) {
    sortSel.addEventListener('change', function () {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSel.value);
      window.location.href = url.toString();
    });
  }
})();
